import React, { useEffect, useState, useRef, createElement, Component } from 'react';
import { useMetaTags } from '../hooks/useMetaTags';
import { trackMetaEvent } from '../hooks/useMetaPixel';
import { useLocation } from 'react-router-dom';
import { ArrowLeftIcon } from 'lucide-react';
import { getPublisherParams } from '../utils/publisherParams';
import { applyUrlPassthrough, pruneEmptyParams } from '../utils/urlPassthrough';
import { toStateAbbr } from '../utils/usStates';
import { getWebLandingURL } from '../utils/landingUrl';
import { degreeLevelOptions, areaStudyOptions, programOptions, getAreaFromProgram, isValidProgram } from '../data/filterOptions';
import { useScreenInit } from '../useScreenInit.js';
import { RequestInfoSplitCard } from '../components/RequestInfoSplitCard';
import { GraduationCapIcon, CompassIcon, BadgeCheckIcon, DollarSignIcon, ClockIcon, BriefcaseIcon, BookOpenIcon, ShieldCheckIcon } from 'lucide-react';
import { trackFormSubmit } from '../utils/trackFormSubmit';
import { injectLeadCertificationScripts, readCertificationFields, waitForTrustedFormCert } from '../utils/leadCertificationScripts';
import { LeadTrackingFields } from '../components/LeadTrackingFields';
import { getMetaCookies, resolveSub3 } from '../utils/metaCookies';
import { validateAddress, validateStreetAddress, validateZipCode, lookupZip } from '../utils/addressValidation';
// Resolves a deep-linked ?mProgram=<id> into the program + its parent area
// of study so the program-selection step can be pre-filled and skipped.
// Returns null when the param is absent or not a known program id.
const resolveProgramFromSearch = (search: string): {
  program: number;
  areaOfStudy: number;
} | null => {
  const raw = new URLSearchParams(search).get('mProgram');
  if (!raw) return null;
  const program = Number(raw);
  if (!Number.isFinite(program) || !isValidProgram(program)) return null;
  return {
    program,
    areaOfStudy: getAreaFromProgram(program)
  };
};
// Deep-link prefill support. Each answer can arrive either as the form's
// own value (e.g. highestEducation=some-college) or as the numeric
// Media-Matchers API code (e.g. highestEducation=5), so partner links can
// reuse whichever vocabulary they already send.
const EDUCATION_VALUES = ['some-high-school', 'high-school', 'some-college', 'associates', 'bachelors', 'masters', 'doctorate'];
const EDUCATION_BY_API_CODE: Record<string, string> = {
  '1': 'high-school',
  '2': 'some-high-school',
  '5': 'some-college',
  '7': 'bachelors'
};
const START_TIMEFRAME_VALUES = ['immediately', '1-3-months', '3-6-months', '6-12-months', '1-year-plus', 'undecided'];
const START_TIMEFRAME_BY_API_CODE: Record<string, string> = {
  '1': 'immediately',
  '2': '1-3-months',
  '3': '3-6-months',
  '4': '6-12-months',
  '5': '1-year-plus',
  '6': 'undecided'
};
const MILITARY_VALUES = ['none', 'active-duty', 'veteran', 'guard', 'spouse', 'dependent', 'unknown'];
// Inbound military codes mirror the partner spec: 1 None, 2 Active Duty,
// 3 Veteran, 4 National Guard, 5 Spouse, 6 Dependent. "Unknown" has no
// code (it's sent blank), so a blank/unrecognized value leaves the
// question unanswered and the user is asked it.
const MILITARY_BY_API_CODE: Record<string, string> = {
  '1': 'none',
  '2': 'active-duty',
  '3': 'veteran',
  '4': 'guard',
  '5': 'spouse',
  '6': 'dependent'
};
const resolveChoice = (raw: string | null, allowed: string[], byApiCode: Record<string, string>): string => {
  if (!raw) return '';
  const value = raw.trim().toLowerCase();
  if (allowed.includes(value)) return value;
  return byApiCode[value] || '';
};
const resolvePrefillFromSearch = (search: string) => {
  const params = new URLSearchParams(search);
  const prefill: {
    degreeLevel?: number;
    areaOfStudy?: number;
    highestEducation?: string;
    hsGraduationYear?: string;
    startDate?: string;
    militaryAffiliation?: string;
    zipCode?: string;
    streetAddress?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  } = {};
  // Degree level — only asked on the area-of-study landings, where the area
  // is fixed by the URL. Certificate=1, Associate's=2, Bachelor's=3,
  // Master's=4; anything else is treated as unanswered.
  const degreeRaw = (params.get('degreeInterest') || params.get('degreeLevel') || '').trim();
  if (degreeRaw) {
    const degree = Number(degreeRaw);
    if (degreeLevelOptions.some(option => option.value === degree)) {
      prefill.degreeLevel = degree;
    }
  }
  // Area of study — prefills the first step's dropdown (the program
  // dropdown still has to be chosen unless mProgram is also supplied).
  const areaRaw = params.get('areaStudy') || params.get('areaOfStudy');
  if (areaRaw) {
    const area = Number(areaRaw);
    if (Number.isFinite(area) && programOptions[area]) {
      prefill.areaOfStudy = area;
    }
  }
  const education = resolveChoice(params.get('highestEducation'), EDUCATION_VALUES, EDUCATION_BY_API_CODE);
  if (education) prefill.highestEducation = education;
  // Graduation year — a 4-digit year inside the dropdown's range. Anything
  // earlier than 1990 collapses into the "1989 or earlier" option (1989), as
  // do the legacy "before-1965" / "before-1990" spellings.
  const gradYearRaw = (params.get('gradYear') || '').trim().toLowerCase();
  if (gradYearRaw) {
    const currentYear = new Date().getFullYear();
    const year = Number(gradYearRaw);
    if (gradYearRaw === 'before-1965' || gradYearRaw === 'before-1990') {
      prefill.hsGraduationYear = '1989';
    } else if (Number.isInteger(year) && year >= 1900 && year <= currentYear) {
      prefill.hsGraduationYear = year < 1990 ? '1989' : String(year);
    }
  }
  const startTimeframe = resolveChoice(params.get('startTimeframe'), START_TIMEFRAME_VALUES, START_TIMEFRAME_BY_API_CODE);
  if (startTimeframe) prefill.startDate = startTimeframe;
  const military = resolveChoice(params.get('military') || params.get('militaryAffiliation'), MILITARY_VALUES, MILITARY_BY_API_CODE);
  if (military) prefill.militaryAffiliation = military;
  const zip = (params.get('zipCode') || '').replace(/\D/g, '').slice(0, 5);
  if (zip.length === 5) prefill.zipCode = zip;
  const address = (params.get('address') || params.get('streetAddress') || '').trim();
  if (address) prefill.streetAddress = address;
  const firstName = params.get('firstName');
  if (firstName) prefill.firstName = firstName;
  const lastName = params.get('lastName');
  if (lastName) prefill.lastName = lastName;
  const email = params.get('email');
  if (email) prefill.email = email;
  return prefill;
};
// Value mapping helpers for Media Matchers API
const educationToApiValue = (formValue: string): number => {
  const map: Record<string, number> = {
    'some-high-school': 2,
    'high-school': 1,
    'some-college': 5,
    associates: 5,
    bachelors: 7,
    masters: 7,
    doctorate: 7
  };
  return map[formValue] || 9; // Unknown
};
const startTimeframeToApiValue = (formValue: string): number => {
  const map: Record<string, number> = {
    immediately: 1,
    '1-3-months': 2,
    '3-6-months': 3,
    '6-12-months': 4,
    '1-year-plus': 4,
    undecided: 6
  };
  return map[formValue] || 6; // Unknown
};
// military: None=1, Active Duty=2, Veteran=3, National Guard=4, Spouse=5,
// Dependent=6. Per the partner spec "Unknown" is sent BLANK rather than as
// a code, so anything unmapped resolves to an empty value.
const militaryAffiliationToApiValue = (formValue: string): string => {
  const map: Record<string, string> = {
    none: '1',
    'active-duty': '2',
    veteran: '3',
    guard: '4',
    spouse: '5',
    dependent: '6'
  };
  return map[formValue] || '';
};
// Sanitize TCPA disclaimer HTML coming back from the Media-Matchers API.
// The API sometimes embeds <a href="…"> links pointing to partner
// privacy policies / terms pages, and we want those to render as real
// clickable links (not literal "<a href=…>" text). We allow only a
// minimal whitelist of inline tags and force every <a> to open in a
// new tab with safe rel attributes.
const sanitizeTcpaHtml = (raw: string): string => {
  if (!raw) return '';
  // Use the browser's own parser to walk the DOM and strip anything
  // outside the whitelist. This is safer than regex-only sanitization
  // because it normalizes weird markup, broken tags, attribute quoting, etc.
  if (typeof document === 'undefined') return raw;
  const ALLOWED_TAGS = new Set(['A', 'BR', 'STRONG', 'B', 'EM', 'I', 'SPAN', 'P']);
  const container = document.createElement('div');
  container.innerHTML = raw;
  const walk = (node: Element) => {
    // Iterate over a static copy because we mutate during traversal.
    Array.from(node.children).forEach(child => {
      if (!ALLOWED_TAGS.has(child.tagName)) {
        // Replace disallowed element with its text content.
        const text = document.createTextNode(child.textContent || '');
        child.replaceWith(text);
        return;
      }
      // Strip all attributes except href on <a> tags.
      Array.from(child.attributes).forEach(attr => {
        if (child.tagName === 'A' && attr.name === 'href') return;
        child.removeAttribute(attr.name);
      });
      // Force safe defaults on every link.
      if (child.tagName === 'A') {
        const href = child.getAttribute('href') || '';
        // Block javascript:, data:, vbscript: URIs.
        if (/^\s*(javascript|data|vbscript):/i.test(href)) {
          child.removeAttribute('href');
        }
        child.setAttribute('target', '_blank');
        child.setAttribute('rel', 'noopener noreferrer');
        // Tailwind-ish underline styling so the link is obvious inside
        // the dense disclaimer paragraph.
        child.setAttribute('class', 'underline text-[#e8594f] hover:text-[#d64a41]');
      }
      walk(child);
    });
  };
  walk(container);
  return container.innerHTML;
};
const generateUUID = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : r & 0x3 | 0x8;
    return v.toString(16);
  });
};
interface FormData {
  degreeLevel: number;
  areaOfStudy: number;
  program: number;
  highestEducation: string;
  hsGraduationYear: string;
  militaryAffiliation: string;
  startDate: string;
  streetAddress: string;
  city: string;
  state: string;
  zipCode: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  consent: boolean;
}
interface SchoolResult {
  name?: string;
  schoolName?: string;
  logoUrl?: string;
  logo?: string;
  programName?: string;
  mRecordID?: string;
  recordID?: string;
  id?: string;
  tcpaText?: string;
  tcpa?: string;
}
// Icons available to the supporting-content cards under the hero.
export const SUPPORT_NOTE_ICONS = {
  degree: GraduationCapIcon,
  compass: CompassIcon,
  license: BadgeCheckIcon,
  money: DollarSignIcon,
  clock: ClockIcon,
  briefcase: BriefcaseIcon,
  book: BookOpenIcon,
  shield: ShieldCheckIcon
} as const;
export type SupportNoteIcon = keyof typeof SUPPORT_NOTE_ICONS;
// Landing-page copy for a template. Every field is optional; anything not
// supplied falls back to the default FindOnlineDegrees messaging.
export interface RequestInformationContent {
  metaTitle?: string;
  metaDescription?: string;
  badge?: string;
  headline?: string;
  headlineAccent?: string;
  subhead?: string;
  stats?: {
    value: string;
    label: string;
  }[];
  features?: string[];
  formHeading?: string;
  formSubhead?: string;
  // Optional full-bleed photo behind the hero. 'dark' (the default) lays
  // light copy over a dark scrim; 'light' keeps dark copy over a bright
  // scrim, for airy photos; 'none' shows the photo untinted.
  heroBackgroundImage?: string;
  heroOverlay?: 'dark' | 'light' | 'none';
  // Mirrors the hero photo horizontally, for when the subject sits on the
  // side the form card covers.
  heroImageFlipped?: boolean;
  // Copy tone over the photo. Defaults to light for the dark scrim and dark
  // otherwise — set it explicitly when an untinted photo is itself dark.
  heroCopy?: 'light' | 'dark';
  // Optional reassurance section rendered in a band under the hero.
  supportNote?: {
    heading: string;
    body: string;
    points?: {
      title: string;
      body: string;
      // Optional icon key — see SUPPORT_NOTE_ICONS below.
      icon?: SupportNoteIcon;
    }[];
    footnote?: string;
  };
}
export interface RequestInformationPageProps {
  // Template variant id. 'default' powers /request-information; named
  // variants (e.g. 'v1') power the /request-information/<variant> template
  // routes so each one can diverge from the default flow over time.
  variant?: string;
  // Optional landing-copy overrides for audience-specific templates.
  content?: RequestInformationContent;
  // 'default' keeps the hero + separate step pages. 'split-card' asks every
  // question inside one persistent card beside the marketing copy.
  layout?: 'default' | 'split-card';
  // Area-of-study landing pages lock the area (it's implied by the page) and
  // ask for degree level + program instead.
  lockedAreaId?: number;
}
export function RequestInformationPage({
  variant = 'default',
  content,
  layout = 'default',
  lockedAreaId
}: RequestInformationPageProps = {}) {
  const location = useLocation();
  // Screens preview: seeds the initial step so each screen renders its stage.
  const screenInit = useScreenInit();
  // Named templates all share the streamlined v1 flow: no degree-level
  // question, no military question, and URL prefill support. Only the
  // original /request-information route keeps the full question set.
  const usesStreamlinedFlow = variant !== 'default';
  // Streamlined templates drop the degree-level question, so the
  // `degreeInterest` parameter is also omitted from every API call and
  // downstream redirect.
  // Area-of-study landings still ask for degree level (the area is fixed by
  // the page), so they keep degreeInterest in the payload.
  const includeDegreeInterest = !usesStreamlinedFlow || Boolean(lockedAreaId);
  // They don't ask about military affiliation either. The answer defaults
  // to "None" (API value 1) so it's still posted with the lead.
  const showMilitaryStep = !usesStreamlinedFlow;
  // Deep links like ?mProgram=100 pre-select the program (and its area of
  // study) on the first step, which is still shown so the user can confirm
  // or change it.
  const deepLinkedProgram = usesStreamlinedFlow ? resolveProgramFromSearch(location.search) : null;
  // Remaining answers can also arrive on the URL (area of study, education,
  // grad year, start timeframe, military, ZIP, and contact fields). Those
  // steps are pre-selected but still shown, so the user can review or
  // change them before continuing.
  const urlPrefill = usesStreamlinedFlow ? resolvePrefillFromSearch(location.search) : null;
  useMetaTags({
    title: content?.metaTitle || 'Find Your Perfect Program | FindOnlineDegrees',
    description: content?.metaDescription || 'Get matched with accredited online degree programs tailored to your goals.'
  });
  const initialFormData: FormData = {
    degreeLevel: urlPrefill?.degreeLevel || 0,
    areaOfStudy: lockedAreaId || deepLinkedProgram?.areaOfStudy || urlPrefill?.areaOfStudy || 0,
    program: deepLinkedProgram?.program || 0,
    highestEducation: urlPrefill?.highestEducation || '',
    hsGraduationYear: urlPrefill?.hsGraduationYear || '',
    militaryAffiliation: urlPrefill?.militaryAffiliation || (showMilitaryStep ? '' : 'none'),
    startDate: urlPrefill?.startDate || '',
    streetAddress: urlPrefill?.streetAddress || '',
    city: '',
    state: '',
    zipCode: urlPrefill?.zipCode || '',
    firstName: urlPrefill?.firstName || '',
    lastName: urlPrefill?.lastName || '',
    email: urlPrefill?.email || '',
    phone: '',
    consent: false
  };
  // The program-selection page normally shows first — even when mProgram
  // pre-selects the program — so the user confirms what they're asking
  // about. Later steps answered by the URL are skipped from there.
  //
  // The exception is an area-of-study landing where the URL supplied BOTH the
  // degree level and the program: every question on that page is already
  // answered, so re-asking it is pure friction and the visitor starts on the
  // first genuinely unanswered question.
  const skipProgramStep = Boolean(lockedAreaId && deepLinkedProgram && urlPrefill?.degreeLevel);
  const [step, setStep] = useState(screenInit.step ?? (skipProgramStep ? 1 : 0));
  // Split-card layout only: step 0 is asked as two screens — area of study
  // (stage 0) then the program within it (stage 1).
  const [stage, setStage] = useState(screenInit.stage ?? (deepLinkedProgram ? 1 : 0));
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [error, setError] = useState('');
  const [searchResults, setSearchResults] = useState<SchoolResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [currentSchoolIndex, setCurrentSchoolIndex] = useState(0);
  const [isSubmittingSchool, setIsSubmittingSchool] = useState(false);
  const [schoolTcpaChecked, setSchoolTcpaChecked] = useState(false);
  const [schoolError, setSchoolError] = useState('');
  // Clicks Search API state — drives the "additional matches" step
  // that runs after the Leads carousel. Same school-card UI as step 7
  // but no TCPA checkbox; clicking the CTA opens the partner's
  // clickURL and advances to the next match.
  const [clicksResults, setClicksResults] = useState<SchoolResult[]>([]);
  const [clicksLoading, setClicksLoading] = useState(false);
  const [clicksIndex, setClicksIndex] = useState(0);
  // TCPA Search/Post API state — drives the contact-step (step 6) consent
  // disclaimer. Disclaimer text comes from the SEARCH API response; the
  // POST API is pinged on final submit with mRecordID + contact info.
  interface TcpaSearchResult {
    tcpaRequestId?: string;
    mRecordID?: string;
    mSessionID?: string;
    mBrandName?: string;
    mBrandID?: string;
    tcpaDisclaimer?: string;
    imageURL?: string;
  }
  const [tcpaResult, setTcpaResult] = useState<TcpaSearchResult | null>(null);
  const [tcpaLoading, setTcpaLoading] = useState(false);
  const [tcpaError, setTcpaError] = useState('');
  // True while the ZIP is being verified on the address step.
  const [addressChecking, setAddressChecking] = useState(false);
  // Stable mSessionID for the lifetime of this page-view. Initialized
  // ONCE from the URL (?mSessionID=…) if provided by the upstream
  // referrer, otherwise generated fresh. All four Media-Matchers API
  // calls (leads search/post, tcpa search/post) use the same value so
  // server-side they can be correlated to a single session.
  const mSessionIdRef = useRef<string>('');
  if (!mSessionIdRef.current) {
    const incoming = new URLSearchParams(location.search).get('mSessionID');
    mSessionIdRef.current = incoming && incoming.trim() ? incoming.trim() : `s-${generateUUID()}`;
  }
  // Stable lead identifier for analytics: the LeadiD token when the script
  // has fired, otherwise a generated id kept for the life of the session.
  const leadIdRef = useRef<string>('');
  const getLeadIdToken = (): string => {
    const {
      leadIdToken
    } = readCertificationFields();
    if (leadIdToken) return leadIdToken;
    if (!leadIdRef.current) leadIdRef.current = generateUUID();
    return leadIdRef.current;
  };
  // Consent certification must accompany every outbound lead, not just the
  // search calls — a Post without these can't be defended in a TCPA dispute.
  // Each vendor value is included only when genuinely issued: an empty or
  // placeholder certificate is worse than none, since it looks like proof.
  const applyCertificationFields = (params: URLSearchParams) => {
    const {
      leadIdToken,
      trustedFormUrl,
      trustedFormPingUrl
    } = readCertificationFields();
    params.set('leadIdToken', getLeadIdToken());
    if (trustedFormUrl) params.set('trustedFormUrl', trustedFormUrl);
    if (trustedFormPingUrl) params.set('trustedFormPingUrl', trustedFormPingUrl);
    // Surfaced so a blank certificate is visible during QA rather than being
    // discovered later by the buyer. jornaya=false means the LeadiD script
    // never fired and the token below is a local fallback with no
    // certification value.
    console.log('[Consent Certification]', {
      jornaya: Boolean(leadIdToken),
      trustedFormCert: trustedFormUrl || '(none issued)',
      trustedFormPing: trustedFormPingUrl || '(none issued)',
      scriptLoaded: Boolean(document.getElementById('trustedform_script')),
      // More than one entry here is the "Lock found" failure: the Certify SDK
      // refuses to run when a second copy is present (commonly a GTM tag), and
      // no certificate is ever issued. Exactly one is required.
      trustedFormScripts: Array.from(document.querySelectorAll('script[src*="trustedform"]')).map(s => (s as HTMLScriptElement).src)
    });
  };
  // Pre-populate the form from URL query params when the user lands here
  // from a partner referrer (e.g. the bridge page) that has already
  // collected first name, last name, and email. We only set values that
  // are present in the URL and don't already have something in state.
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const prefill: Partial<Record<keyof FormData, string>> = {};
    const firstName = urlParams.get('firstName');
    const lastName = urlParams.get('lastName');
    const email = urlParams.get('email');
    if (firstName) prefill.firstName = firstName;
    if (lastName) prefill.lastName = lastName;
    if (email) prefill.email = email;
    if (Object.keys(prefill).length > 0) {
      console.log('[Prefill] applying URL params:', prefill);
      setFormData(prev => ({
        ...prev,
        ...prefill
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    fetchUserLocation();
  }, []);
  // If the user lands on the address step and the geo lookup never
  // populated city/state/zip (first attempt failed silently, or providers
  // were blocked at page load), try again so the address fields can be
  // pre-filled before the user has to type them.
  useEffect(() => {
    if (step !== 5) return;
    if (formData.zipCode && formData.city && formData.state) return;
    console.log('[Geo] re-attempting IP lookup on address step');
    fetchUserLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);
  // Inject LeadID + TrustedForm tracking scripts on mount.
  // Both scripts write their values into hidden form fields rendered below
  // (name="leadid_token" and name="xxTrustedFormCertUrl").
  useEffect(() => {
    injectLeadCertificationScripts();
  }, []);
  // IP → ZIP lookup with multi-provider fallback. ipapi.co is the first
  // choice but it rate-limits aggressively (1k/day per IP) and returns
  // opaque 429s that surface as `Failed to fetch` in the browser. We fall
  // through to ipwho.is and ipinfo.io if earlier providers fail.
  const fetchUserLocation = async () => {
    interface GeoResult {
      city: string;
      state: string;
      zip: string;
    }
    const providers: {
      name: string;
      fetch: () => Promise<GeoResult | null>;
    }[] = [{
      name: 'ipapi.co',
      fetch: async () => {
        const res = await fetch('https://ipapi.co/json/');
        if (!res.ok) throw new Error(`ipapi.co ${res.status}`);
        const data = await res.json();
        if (data?.error) throw new Error(`ipapi.co: ${data.reason}`);
        return {
          city: data.city || '',
          state: data.region_code || '',
          zip: data.postal || ''
        };
      }
    }, {
      name: 'ipwho.is',
      fetch: async () => {
        const res = await fetch('https://ipwho.is/');
        if (!res.ok) throw new Error(`ipwho.is ${res.status}`);
        const data = await res.json();
        if (data?.success === false) throw new Error(`ipwho.is: ${data.message}`);
        return {
          city: data.city || '',
          state: data.region_code || data.region || '',
          zip: data.postal || ''
        };
      }
    }, {
      name: 'ipinfo.io',
      fetch: async () => {
        const res = await fetch('https://ipinfo.io/json');
        if (!res.ok) throw new Error(`ipinfo.io ${res.status}`);
        const data = await res.json();
        return {
          city: data.city || '',
          state: data.region || '',
          zip: data.postal || ''
        };
      }
    }];
    for (const p of providers) {
      try {
        const result = await p.fetch();
        if (result && (result.zip || result.city || result.state)) {
          console.log(`[Geo] resolved via ${p.name}`, result);
          setFormData(prev => ({
            ...prev,
            // Don't overwrite anything the user has already typed
            city: prev.city || result.city,
            state: prev.state || result.state,
            zipCode: prev.zipCode || result.zip
          }));
          return;
        }
      } catch (err) {
        console.warn(`[Geo] ${p.name} failed:`, err);
      }
    }
    console.warn('[Geo] all IP geolocation providers failed');
  };
  // Shares the cached lookup used by address validation, so verifying on
  // Continue doesn't re-request a ZIP already resolved here.
  const lookupZipCode = async (zip: string) => {
    if (zip.length !== 5) return;
    const place = await lookupZip(zip);
    if (place) {
      updateField('city', place.city);
      updateField('state', place.state);
    }
  };
  const updateField = (field: keyof FormData, value: string | number | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
  };
  // JSONP helper — bypasses CORS by loading the API as a script tag.
  // The API must support a callback query param (most lead APIs do).
  const jsonpRequest = <T,>(url: string, timeoutMs = 8000): Promise<T> => {
    return new Promise((resolve, reject) => {
      const callbackName = `mmcb_${Date.now()}_${Math.floor(Math.random() * 1e6)}`;
      const script = document.createElement('script');
      let settled = false;
      const cleanup = () => {
        try {
          delete (window as any)[callbackName];
        } catch {
          ;
          (window as any)[callbackName] = undefined;
        }
        if (script.parentNode) script.parentNode.removeChild(script);
      };
      (window as any)[callbackName] = (data: T) => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve(data);
      };
      const sep = url.includes('?') ? '&' : '?';
      script.src = `${url}${sep}callback=${callbackName}`;
      script.async = true;
      script.onerror = () => {
        if (settled) return;
        settled = true;
        cleanup();
        reject(new Error('JSONP request failed'));
      };
      const timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        cleanup();
        reject(new Error('JSONP request timed out'));
      }, timeoutMs);
      (window as any)[callbackName] = (origCb => (data: T) => {
        clearTimeout(timer);
        origCb(data);
      })((window as any)[callbackName]);
      document.body.appendChild(script);
    });
  };
  // NOTE: `excludeBrand` is deliberately NOT sent on either search. Passing
  // the TCPA brand made the Clicks Search return no matches, which dropped
  // users straight onto the listings page after submitting. Duplicate offers
  // are the lesser problem, so both searches now run unfiltered.
  // Contact details are only meaningful once the user has entered them, so
  // each field is added only when it has a value. Keeps the Search pings
  // clean when they fire before the contact step has been completed.
  const applyContactFields = (params: URLSearchParams) => {
    const phoneDigits = formData.phone.replace(/\D/g, '');
    if (formData.firstName.trim()) params.set('firstName', formData.firstName.trim());
    if (formData.lastName.trim()) params.set('lastName', formData.lastName.trim());
    if (formData.email.trim()) params.set('email', formData.email.trim());
    if (phoneDigits) params.set('phone', phoneDigits);
  };
  const callSearchAPI = async () => {
    setSearchLoading(true);
    const urlParams = new URLSearchParams(location.search);
    // leadIdToken + trustedFormUrl are added by applyCertificationFields()
    // below, which reads the values the LeadiD / TrustedForm scripts wrote
    // into their hidden inputs.
    const searchParams = new URLSearchParams({
      degreeInterest: String(formData.degreeLevel),
      areaStudy: String(formData.areaOfStudy),
      mProgram: String(formData.program),
      highestEducation: String(educationToApiValue(formData.highestEducation)),
      gradYear: formData.hsGraduationYear,
      militaryAffiliation: String(militaryAffiliationToApiValue(formData.militaryAffiliation)),
      startTimeframe: String(startTimeframeToApiValue(formData.startDate)),
      sub1: urlParams.get('sub1') || '',
      sub2: urlParams.get('sub2') || '',
      sub3: resolveSub3(location.search),
      zipCode: formData.zipCode,
      address: formData.streetAddress,
      city: formData.city,
      state: toStateAbbr(formData.state),
      webLandingURL: getWebLandingURL(),
      campusType: urlParams.get('campusType') || '1',
      mediaType: urlParams.get('mediaType') || '1',
      mSessionID: mSessionIdRef.current
    });
    // The Leads Search now runs after submit, so the contact fields are
    // available — send them when present, skip them if this ever fires early.
    applyContactFields(searchParams);
    applyCertificationFields(searchParams);
    // Forward any other params the traffic source put on the landing URL
    // (utm_*, fbclid, clickid, partner-specific keys) without clobbering the
    // form's own answers.
    applyUrlPassthrough(searchParams, location.search);
    Object.entries(getPublisherParams(location.search, {
      mPubGroupID: 'PCGP4731148320'
    })).forEach(([k, v]) => searchParams.set(k, v));
    if (!includeDegreeInterest) {
      searchParams.delete('degreeInterest');
    }
    const url = `https://leads.media-matchers.com/search?${pruneEmptyParams(searchParams).toString()}`;
    console.log('[Leads Search API] PING:', url);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetch(url, {
        signal: controller.signal
      });
      clearTimeout(timeout);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data: any = await response.json();
      console.log('[Leads Search API] response:', data);
      const schools = data?.results || data?.schools || data?.data || data?.result || (Array.isArray(data) ? data : []) || [];
      // Drop "ghost" entries that don't have a usable brand name — these
      // would otherwise render as a generic "Partner School" card with
      // an "Online Degree Program" pill and a templated TCPA disclaimer.
      const validSchools = Array.isArray(schools) ? schools.filter((s: any) => s?.mBrandName || s?.brandName || s?.name || s?.schoolName) : [];
      console.log(`[Leads Search API] ${validSchools.length} valid of ${Array.isArray(schools) ? schools.length : 0} returned schools`);
      setSearchResults(validSchools);
    } catch (err) {
      clearTimeout(timeout);
      console.error('Search API error:', err);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };
  // TCPA Search API — fires when user reaches the contact step. Result
  // populates the consent disclaimer text.
  const callTcpaSearchAPI = async () => {
    setTcpaLoading(true);
    setTcpaError('');
    setTcpaResult(null);
    // This is the earliest outbound call in the flow, and a visitor who
    // arrives on a prefilled deep link can reach the contact step before
    // TrustedForm has issued its certificate. Give it a moment so the consent
    // record isn't created without one.
    await waitForTrustedFormCert();
    const urlParams = new URLSearchParams(location.search);
    const searchParams = new URLSearchParams({
      degreeInterest: String(formData.degreeLevel),
      areaStudy: String(formData.areaOfStudy),
      mProgram: String(formData.program),
      highestEducation: String(educationToApiValue(formData.highestEducation)),
      gradYear: formData.hsGraduationYear,
      militaryAffiliation: String(militaryAffiliationToApiValue(formData.militaryAffiliation)),
      startTimeframe: String(startTimeframeToApiValue(formData.startDate)),
      sub1: urlParams.get('sub1') || '',
      sub2: urlParams.get('sub2') || '',
      sub3: resolveSub3(location.search),
      zipCode: formData.zipCode,
      address: formData.streetAddress,
      city: formData.city,
      state: toStateAbbr(formData.state),
      webLandingURL: getWebLandingURL(),
      campusType: urlParams.get('campusType') || '1',
      mediaType: urlParams.get('mediaType') || '2',
      mSessionID: mSessionIdRef.current
    });
    applyContactFields(searchParams);
    applyCertificationFields(searchParams);
    applyUrlPassthrough(searchParams, location.search);
    Object.entries(getPublisherParams(location.search, {
      mPubGroupID: 'PCGP4731148320'
    })).forEach(([k, v]) => searchParams.set(k, v));
    if (!includeDegreeInterest) {
      searchParams.delete('degreeInterest');
    }
    const url = `https://tcpa.media-matchers.com/search?${pruneEmptyParams(searchParams).toString()}`;
    console.log('[TCPA Search API] PING (GET):', url);
    // Primary path: plain GET via fetch. If CORS blocks the response,
    // fall back to JSONP (the API must support ?callback=…). Both paths
    // are GET requests so the server-side handling is identical.
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetch(url, {
        signal: controller.signal
      });
      clearTimeout(timeout);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data: TcpaSearchResult = await response.json();
      console.log('[TCPA Search API] response (fetch):', data);
      setTcpaResult(data);
      setTcpaLoading(false);
      return data.mBrandID || '';
    } catch (fetchErr) {
      clearTimeout(timeout);
      console.warn('[TCPA Search API] fetch failed, falling back to JSONP:', fetchErr);
      try {
        const data = await jsonpRequest<TcpaSearchResult>(url, 8000);
        console.log('[TCPA Search API] response (jsonp):', data);
        setTcpaResult(data);
        setTcpaLoading(false);
        return data.mBrandID || '';
      } catch (jsonpErr) {
        console.error('[TCPA Search API] both fetch and JSONP failed:', jsonpErr);
        setTcpaError('Unable to load consent terms. Please refresh and try again.');
        setTcpaLoading(false);
        return '';
      }
    }
  };
  // TCPA Post API — fired on final submit with the mRecordID from the
  // TCPA search response and the user's contact info.
  //
  // Method: GET. We send the request THREE ways for maximum reliability,
  // because the user navigates to step 7 immediately after — and some
  // browsers cancel in-flight requests when the originating DOM element
  // is garbage-collected or the page re-renders:
  //   1) fetch(url, { keepalive: true, mode: 'no-cors' }) — survives
  //      navigation/unload and bypasses CORS preflight.
  //   2) <img> pixel appended to <body> — keeps the element alive long
  //      enough for the network request to complete.
  //   3) JSONP <script> tag — guaranteed visible in Network as a script
  //      request; works even if (1) and (2) are blocked.
  const callTcpaPostAPI = (onConfirmed?: () => void) => {
    const recordID = tcpaResult?.mRecordID;
    if (!recordID) {
      console.warn('[TCPA Post API] skipped — no mRecordID');
      return;
    }
    // Only report the submission once, from whichever transport confirms
    // first (keepalive fetch, pixel, or JSONP).
    let confirmed = false;
    const confirm = () => {
      if (confirmed) return;
      confirmed = true;
      onConfirmed?.();
    };
    const urlParams = new URLSearchParams(location.search);
    const phoneDigits = formData.phone.replace(/\D/g, '');
    // Meta match keys for server-side (CAPI) attribution.
    const {
      fbp,
      fbc
    } = getMetaCookies();
    const postParams = new URLSearchParams({
      mRecordID: recordID,
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: phoneDigits,
      sub1: urlParams.get('sub1') || '',
      sub2: urlParams.get('sub2') || '',
      sub3: resolveSub3(location.search),
      webLandingURL: getWebLandingURL(),
      mSessionID: mSessionIdRef.current
    });
    if (fbp) postParams.set('fbp', fbp);
    if (fbc) postParams.set('fbc', fbc);
    applyCertificationFields(postParams);
    // Publisher identification — same mapping as the Search calls:
    // URL mPubGroupID, or mPubID + mPubCampaignID, else the default group.
    applyUrlPassthrough(postParams, location.search);
    Object.entries(getPublisherParams(location.search, {
      mPubGroupID: 'PCGP4731148320'
    })).forEach(([k, v]) => postParams.set(k, v));
    const url = `https://tcpa.media-matchers.com/post?${pruneEmptyParams(postParams).toString()}`;
    console.log('[TCPA Post API] PING (GET):', url);
    // (1) keepalive fetch — survives page navigation
    try {
      fetch(url, {
        method: 'GET',
        mode: 'no-cors',
        keepalive: true
      }).then(() => confirm()).catch(err => console.warn('[TCPA Post API] fetch error:', err));
    } catch (err) {
      console.warn('[TCPA Post API] fetch threw:', err);
    }
    // (2) Image pixel — attached to body so it isn't GC'd mid-request
    try {
      const pixel = new Image();
      pixel.style.display = 'none';
      pixel.onload = () => {
        console.log('[TCPA Post API] pixel loaded');
        confirm();
      };
      pixel.onerror = () => console.warn('[TCPA Post API] pixel onerror (request may still have hit the server)');
      pixel.src = url;
      document.body.appendChild(pixel);
    } catch (err) {
      console.warn('[TCPA Post API] pixel error:', err);
    }
    // (3) JSONP fallback — guaranteed visible network request
    try {
      const script = document.createElement('script');
      script.src = url;
      script.async = true;
      script.onload = () => {
        console.log('[TCPA Post API] jsonp loaded');
        confirm();
        script.parentNode?.removeChild(script);
      };
      script.onerror = () => {
        console.warn('[TCPA Post API] jsonp onerror (request still went out)');
        script.parentNode?.removeChild(script);
      };
      document.body.appendChild(script);
    } catch (err) {
      console.warn('[TCPA Post API] jsonp error:', err);
    }
  };
  // Clicks Search API — fires after the user enters the Leads carousel
  // so results are ready by the time they finish. Returns partner
  // schools with a `clickURL` field; the user is redirected to that URL
  // when they click through. Same form payload as the Leads search.
  // No excludeBrand is sent — see the note above callSearchAPI.
  const callClicksSearchAPI = async () => {
    setClicksLoading(true);
    setClicksResults([]);
    const urlParams = new URLSearchParams(location.search);
    const searchParams = new URLSearchParams({
      degreeInterest: String(formData.degreeLevel),
      areaStudy: String(formData.areaOfStudy),
      mProgram: String(formData.program),
      highestEducation: String(educationToApiValue(formData.highestEducation)),
      gradYear: formData.hsGraduationYear,
      militaryAffiliation: String(militaryAffiliationToApiValue(formData.militaryAffiliation)),
      startTimeframe: String(startTimeframeToApiValue(formData.startDate)),
      sub1: urlParams.get('sub1') || '',
      sub2: urlParams.get('sub2') || '',
      sub3: resolveSub3(location.search),
      zipCode: formData.zipCode,
      address: formData.streetAddress,
      city: formData.city,
      state: toStateAbbr(formData.state),
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone.replace(/\D/g, ''),
      webLandingURL: getWebLandingURL(),
      // The partner uses campusType + mediaType to select which inventory to
      // match against — without them the Clicks Search returns nothing. A
      // value on the landing URL still wins.
      campusType: urlParams.get('campusType') || '1',
      mediaType: urlParams.get('mediaType') || '2',
      mSessionID: mSessionIdRef.current
    });
    applyCertificationFields(searchParams);
    applyUrlPassthrough(searchParams, location.search);
    Object.entries(getPublisherParams(location.search, {
      mPubGroupID: 'PCGP4731148320'
    })).forEach(([k, v]) => searchParams.set(k, v));
    if (!includeDegreeInterest) {
      searchParams.delete('degreeInterest');
    }
    const url = `https://api.media-matchers.com/search?${pruneEmptyParams(searchParams).toString()}`;
    console.log('[Clicks Search API] PING (GET):', url);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetch(url, {
        signal: controller.signal
      });
      clearTimeout(timeout);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data: any = await response.json();
      console.log('[Clicks Search API] response (fetch):', data);
      const schools = data?.results || data?.schools || data?.data || data?.result || (Array.isArray(data) ? data : []) || [];
      setClicksResults(Array.isArray(schools) ? schools : []);
    } catch (fetchErr) {
      clearTimeout(timeout);
      console.warn('[Clicks Search API] fetch failed, falling back to JSONP:', fetchErr);
      try {
        const data: any = await jsonpRequest(url, 8000);
        console.log('[Clicks Search API] response (jsonp):', data);
        const schools = data?.results || data?.schools || data?.data || data?.result || (Array.isArray(data) ? data : []) || [];
        setClicksResults(Array.isArray(schools) ? schools : []);
      } catch (jsonpErr) {
        console.error('[Clicks Search API] both fetch and JSONP failed:', jsonpErr);
        setClicksResults([]);
      }
    } finally {
      setClicksLoading(false);
    }
  };
  const callPostAPI = async (school: any) => {
    const recordID = school.mRecordID || school.recordID || school.mBrandID || school.brandID || school.id || '';
    if (!recordID) return;
    const urlParams = new URLSearchParams(location.search);
    const phoneDigits = formData.phone.replace(/\D/g, '');
    const {
      fbp,
      fbc
    } = getMetaCookies();
    const postParams = new URLSearchParams({
      mRecordID: recordID,
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: phoneDigits,
      sub1: urlParams.get('sub1') || '',
      sub2: urlParams.get('sub2') || '',
      sub3: resolveSub3(location.search),
      webLandingURL: getWebLandingURL(),
      mSessionID: mSessionIdRef.current
    });
    if (fbp) postParams.set('fbp', fbp);
    if (fbc) postParams.set('fbc', fbc);
    applyCertificationFields(postParams);
    // Publisher identification — same mapping as the Search calls:
    // URL mPubGroupID, or mPubID + mPubCampaignID, else the default group.
    applyUrlPassthrough(postParams, location.search);
    Object.entries(getPublisherParams(location.search, {
      mPubGroupID: 'PCGP4731148320'
    })).forEach(([k, v]) => postParams.set(k, v));
    // Fire-and-forget via image pixel — bypasses CORS since we don't need
    // to read the response. Falls back silently on error.
    try {
      const pixel = new Image();
      pixel.src = `https://leads.media-matchers.com/post?${pruneEmptyParams(postParams).toString()}`;
    } catch (err) {
      console.error('Post API error:', err);
    }
  };
  // Advance past any of the single-question steps (1–4) that the URL
  // already answered, so the user only sees what's still missing.
  const advanceTo = (target: number): number => {
    if (!usesStreamlinedFlow) return target;
    const answered: Record<number, boolean> = {
      1: Boolean(formData.highestEducation),
      2: Boolean(formData.hsGraduationYear),
      3: Boolean(formData.startDate),
      4: Boolean(formData.militaryAffiliation)
    };
    let next = target;
    while (next >= 1 && next <= 4 && answered[next]) next += 1;
    return next;
  };
  const handleContinue = async () => {
    if (step === 0) {
      if (includeDegreeInterest && !formData.degreeLevel) {
        setError('Please select a degree of interest');
        return;
      }
      if (!formData.areaOfStudy) {
        setError('Please select an area of study');
        return;
      }
      // Split-card layout asks for the area first, then the program.
      if (layout === 'split-card' && stage === 0) {
        setStage(1);
        setError('');
        return;
      }
      if (!formData.program) {
        setError('Please select a program');
        return;
      }
      setStep(advanceTo(1));
      return;
    }
    if (step === 1) {
      if (!formData.highestEducation) {
        setError('Please select your highest level of education');
        return;
      }
      setStep(advanceTo(2));
      return;
    }
    if (step === 2) {
      if (!formData.hsGraduationYear) {
        setError('Please select when you graduated high school');
        return;
      }
      setStep(advanceTo(3));
      return;
    }
    if (step === 3) {
      if (!formData.startDate) {
        setError('Please select when you would like to start classes');
        return;
      }
      setStep(advanceTo(4));
      return;
    }
    if (step === 4) {
      if (!formData.militaryAffiliation) {
        setError('Please select your military affiliation');
        return;
      }
      setStep(5);
      return;
    }
    if (step === 5) {
      // Verify the street line looks like an address and the ZIP actually
      // exists before the lead is posted. A lookup outage never blocks.
      setAddressChecking(true);
      const check = await validateAddress({
        streetAddress: formData.streetAddress,
        zipCode: formData.zipCode,
        state: formData.state
      });
      setAddressChecking(false);
      if (!check.valid) {
        setError(check.message);
        return;
      }
      if (check.place) {
        setFormData(prev => ({
          ...prev,
          city: prev.city || check.place!.city,
          state: prev.state || check.place!.state
        }));
      }
      // Only the TCPA search runs here — it returns the disclosure text
      // rendered on the contact step. The Leads search waits for submit.
      startMatchSearches();
      setStep(6);
      return;
    }
    if (step === 6) {
      if (!formData.firstName.trim()) {
        setError('Please enter your first name');
        return;
      }
      if (!formData.lastName.trim()) {
        setError('Please enter your last name');
        return;
      }
      if (!formData.email.trim()) {
        setError('Please enter your email address');
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        setError('Please enter a valid email address');
        return;
      }
      if (!formData.phone.trim()) {
        setError('Please enter your phone number');
        return;
      }
      if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
        setError('Please enter a valid 10-digit phone number');
        return;
      }
      if (!formData.consent) {
        setError('Please agree to be contacted');
        return;
      }
      handleSubmit();
      return;
    }
  };
  const handleBack = () => {
    setError('');
    if (layout === 'split-card') {
      // Program screen steps back to the area-of-study screen; the first
      // question after step 0 returns to the program screen.
      if (step === 0) {
        if (stage === 1) setStage(0);
        return;
      }
      if (step === 1) {
        setStage(1);
        setStep(0);
        return;
      }
    }
    if (step > 0 && step <= 6) {
      // Step 4 (military) isn't part of the v1 flow, so step over it.
      const previous = step - 1;
      setStep(!showMilitaryStep && previous === 4 ? 3 : previous);
      setError('');
    }
  };
  // Auto-advance when single-select steps get a value. The split-card
  // layout always waits for an explicit Continue click instead.
  const autoAdvance = layout === 'default';
  useEffect(() => {
    if (autoAdvance && step === 1 && formData.highestEducation) {
      const timer = setTimeout(() => {
        setError('');
        setStep(2);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [formData.highestEducation, step]);
  useEffect(() => {
    if (autoAdvance && step === 2 && formData.hsGraduationYear) {
      const timer = setTimeout(() => {
        setError('');
        setStep(3);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [formData.hsGraduationYear, step]);
  useEffect(() => {
    if (autoAdvance && step === 3 && formData.startDate) {
      const timer = setTimeout(() => {
        setError('');
        setStep(showMilitaryStep ? 4 : 5);
      }, 400);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.startDate, step]);
  useEffect(() => {
    if (autoAdvance && step === 4 && formData.militaryAffiliation) {
      const timer = setTimeout(() => {
        setError('');
        setStep(5);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [formData.militaryAffiliation, step]);
  // The TCPA consent lookup fires when the contact step is reached, because
  // its response supplies the disclosure text the user has to agree to.
  // The Leads Search does NOT run here — it waits until the form is
  // submitted, so no match request is made for an incomplete lead.
  const searchesStartedRef = useRef(false);
  const startMatchSearches = () => {
    if (searchesStartedRef.current) return;
    searchesStartedRef.current = true;
    callTcpaSearchAPI();
  };
  useEffect(() => {
    if (step !== 6) return;
    startMatchSearches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);
  // A URL that supplies both address and ZIP answers the address step, so
  // v1 moves straight on to contact details.
  // Only skip when the deep-linked values pass the same format checks the
  // step itself enforces, so a bad URL surfaces the question instead of
  // silently posting a junk address.
  const addressFromUrlRef = useRef(Boolean(urlPrefill?.streetAddress && urlPrefill?.zipCode && validateStreetAddress(urlPrefill.streetAddress).valid && validateZipCode(urlPrefill.zipCode).valid));
  useEffect(() => {
    if (!usesStreamlinedFlow || step !== 5 || !addressFromUrlRef.current) return;
    setStep(6);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);
  // Resolve city/state for a deep-linked ZIP so the payload is complete
  // even when the user never sees the address step.
  useEffect(() => {
    if (!urlPrefill?.zipCode) return;
    lookupZipCode(urlPrefill.zipCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Format phone number as XXX-XXX-XXXX
  const formatPhoneNumber = (value: string) => {
    const digits = value.replace(/\D/g, '');
    if (digits.startsWith('1')) {
      return formData.phone;
    }
    const limited = digits.slice(0, 10);
    if (limited.length <= 3) {
      return limited;
    } else if (limited.length <= 6) {
      return `${limited.slice(0, 3)}-${limited.slice(3)}`;
    } else {
      return `${limited.slice(0, 3)}-${limited.slice(3, 6)}-${limited.slice(6)}`;
    }
  };
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    updateField('phone', formatted);
  };
  const handleSubmit = () => {
    console.log('Form submitted:', formData);
    // The conversion is reported through GTM (dataLayer `form_submission`)
    // once the lead post is confirmed — no Meta Pixel event is fired here.
    setTimeout(() => {
      // Fire the TCPA Post API now that the user has checked the consent
      // box and provided their contact info. The dataLayer `form_submission`
      // event is pushed only once the lead post is confirmed.
      callTcpaPostAPI(() => {
        trackFormSubmit({
          formId: 'request-information',
          pageUrl: window.location.pathname,
          leadId: getLeadIdToken()
        });
      });
      // Leads Search runs now — after every field is collected and the form
      // has been submitted — so the matches are requested against a complete
      // lead. Step 7 shows its own loading state while this resolves.
      callSearchAPI();
      // Kick off the Clicks Search API in the background so the
      // "additional matches" step (8) has results ready by the time the
      // user finishes the Leads carousel.
      callClicksSearchAPI();
      // Move to step 7 (TCPA per school) instead of redirecting immediately
      setStep(7);
    }, 350);
  };
  const handleSchoolAgree = async () => {
    if (!schoolTcpaChecked) {
      setSchoolError('Please check the box to consent before continuing.');
      return;
    }
    const currentSchool = searchResults[currentSchoolIndex];
    // Meta Pixel: Search — the user clicked through a school in the carousel.
    // Only the school brand is sent. Program / area-of-study are deliberately
    // omitted: per Meta's Business Tools Terms §1.h, event parameters must not
    // reflect or imply sensitive categories (e.g. health or mental health),
    // and several study areas would do exactly that.
    trackMetaEvent('Search', {
      search_string: (currentSchool as any)?.mBrandName || (currentSchool as any)?.brandName || (currentSchool as any)?.name || ''
    });
    setIsSubmittingSchool(true);
    await callPostAPI(currentSchool);
    setIsSubmittingSchool(false);
    advanceToNextSchool();
  };
  const handleSchoolSkip = () => {
    advanceToNextSchool();
  };
  const advanceToNextSchool = () => {
    setSchoolTcpaChecked(false);
    setSchoolError('');
    if (currentSchoolIndex < searchResults.length - 1) {
      setCurrentSchoolIndex(currentSchoolIndex + 1);
    } else {
      // Leads carousel done → advance to the Clicks "additional matches"
      // step. If no Clicks results are available we'll redirect home
      // from inside the step 8 render block.
      setStep(8);
    }
  };
  // Query string used when the flow finishes and the user is sent back to
  // the home/listings page. Variants without the degree-level question
  // (v1) forward only the selected program.
  const buildExitParams = () => {
    const params = new URLSearchParams({
      mProgram: formData.program.toString()
    });
    if (includeDegreeInterest) {
      params.set('degreeInterest', formData.degreeLevel.toString());
    }
    return params;
  };
  // Step 8 — Clicks "additional matches" handler. Opens the partner's
  // clickURL in a new tab and advances to the next school. When the
  // list is exhausted we send the user back to the home/listings page.
  const handleClickThrough = () => {
    const school: any = clicksResults[clicksIndex];
    // Meta Pixel: Search — the user clicked through a school in the
    // "additional matches" carousel.
    // Only the school brand is sent — see the note in handleSchoolAgree.
    trackMetaEvent('Search', {
      search_string: school?.mBrandName || school?.brandName || school?.name || ''
    });
    const clickURL = school?.clickURL || school?.clickUrl || school?.url || school?.link;
    if (clickURL) {
      try {
        window.open(clickURL, '_blank', 'noopener,noreferrer');
      } catch (err) {
        console.warn('[Clicks] failed to open clickURL:', err);
      }
    } else {
      console.warn('[Clicks] no clickURL found on school:', school);
    }
    if (clicksIndex < clicksResults.length - 1) {
      setClicksIndex(clicksIndex + 1);
    } else {
      window.location.href = `/?${buildExitParams().toString()}`;
    }
  };
  const handleClicksSkip = () => {
    if (clicksIndex < clicksResults.length - 1) {
      setClicksIndex(clicksIndex + 1);
    } else {
      window.location.href = `/?${buildExitParams().toString()}`;
    }
  };
  const availablePrograms = formData.areaOfStudy ? programOptions[formData.areaOfStudy] || [] : [];
  // Hero copy flips to light type only over the dark scrim. 'light' and
  // 'none' keep the default dark type.
  const hasHeroImage = Boolean(content?.heroBackgroundImage) && (content?.heroCopy ? content.heroCopy === 'light' : (content?.heroOverlay ?? 'dark') === 'dark');
  const currentYear = new Date().getFullYear();
  // Years run back to 1990; everything earlier is collapsed into the
  // "1989 or earlier" option, which posts 1989.
  const years = Array.from({
    length: currentYear - 1989
  }, (_, i) => currentYear - i);
  const selectStyle = {
    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
    backgroundPosition: 'right 0.5rem center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '1.5em 1.5em',
    paddingRight: '2.5rem'
  };
  // Step 7: TCPA per school
  if (step === 7) {
    // If search is still loading, show spinner
    if (searchLoading) {
      return <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-id="element-2179" data-mp="23zp52zhbe7c1">
          <div className="text-center" data-id="element-2180" data-mp="28y0g3djcinvu">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#e8594f] mb-4" data-id="element-2181" data-mp="28wyr055hw0v4"></div>
            <p className="text-xl text-gray-700 font-semibold" data-id="element-2182" data-mp="1z0vr75y48ebc">
              Finding your school matches...
            </p>
          </div>
        </div>;
    }
    // If Leads returned no usable schools, skip the Leads carousel
    // entirely and jump directly to the Clicks carousel (step 8). The
    // Clicks Search has already been fired in the background by
    // handleSubmit, so it will either be ready or still loading by the
    // time step 8 renders.
    if (!searchResults || searchResults.length === 0) {
      console.log('[Step 7] no valid Leads schools — skipping to Clicks carousel (step 8)');
      setStep(8);
      return null;
    }
    const currentSchool: any = searchResults[currentSchoolIndex];
    const schoolName = currentSchool.mBrandName || currentSchool.brandName || currentSchool.name || currentSchool.schoolName || 'Partner School';
    const schoolLogo = currentSchool.imageURL || currentSchool.imageUrl || currentSchool.logoUrl || currentSchool.logo;
    const programName = currentSchool.exactProgramName || currentSchool.matchingProgramName || currentSchool.programName || 'Online Degree Program';
    const schoolHighlight = currentSchool.schoolHighlight || '';
    const aboutText = currentSchool.aboutBrand || currentSchool.aboutCollege || currentSchool.shortDescription || '';
    const richHtml = currentSchool.bodyContent || '';
    const tcpaText = currentSchool.tcpaDisclaimer || currentSchool.tcpaText || currentSchool.tcpa || currentSchool.disclosure || `By checking the box and clicking "I Agree & Continue," you consent to be contacted by ${schoolName} and its representatives regarding educational opportunities via email, phone, or text message using automated technology. Message and data rates may apply. Your consent is not required as a condition of enrollment.`;
    return <div className="min-h-screen bg-gray-50" data-id="element-2183" data-mp="1zyglok837hk2">
        <div className="max-w-3xl mx-auto px-4 py-12" data-id="element-2184" data-mp="200tb1ig6cu75">
          {/* Progress indicator */}
          <div className="text-center mb-8" data-id="element-2185" data-mp="1c9e2v91pvinz">
            <p className="text-sm text-gray-600 font-semibold" data-id="element-2186" data-mp="17ako7dap1lm8">
              School {currentSchoolIndex + 1} of {searchResults.length}
            </p>
            <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden max-w-xs mx-auto" data-id="element-2187" data-mp="1m8mtbrxdif5l">
              <div className="h-full bg-[#e8594f] transition-all duration-300" style={{
              width: `${(currentSchoolIndex + 1) / searchResults.length * 100}%`
            }} data-id="element-2188" data-mp="1h6xlsm9d0jm9" />
            </div>
          </div>

          {/* School card */}
          <div className="relative bg-white rounded-xl shadow-sm border border-gray-200 p-8 md:p-10" data-id="element-2189" data-mp="306dhwdgzqdos">
            {schoolHighlight && <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100 text-amber-900 text-xs font-semibold border border-amber-200 shadow-sm" data-id="element-2190" data-mp="356sx7aku1v9e">
                <span aria-hidden="true" data-id="element-2191" data-mp="2v8vc2vy9sel0">✨</span>
                {schoolHighlight}
              </span>}
            <div className="text-center mb-8" data-id="element-2192" data-mp="3iyplt4naln11">
              {schoolLogo && <img src={schoolLogo} alt={schoolName} className="h-28 md:h-32 mx-auto mb-6 object-contain" data-id="element-2193" data-mp="3nw7vwyd1q8am" />}
              {aboutText && <p className="text-base text-gray-600 leading-relaxed max-w-xl mx-auto" data-id="element-2194" data-mp="3a41gjehpw5c8">
                  {aboutText}
                </p>}
              <div className="mt-4 inline-flex flex-col items-center" data-id="element-2195" data-mp="3e4v1q03rz61f">
                <span className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold mb-1" data-id="element-2196" data-mp="5q3060tkz17d">
                  Your matching program
                </span>
                <span className="inline-block px-4 py-1.5 rounded-full bg-[#e8594f]/10 text-[#e8594f] font-bold text-sm" data-id="element-2197" data-mp="ke3dkvazs2il">
                  {programName}
                </span>
              </div>
            </div>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-3" data-id="element-2198" data-mp="bc3u7wci8mch">
              {schoolName}
            </h2>

            {richHtml && <div className="prose prose-sm max-w-none text-gray-700 mb-6 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mb-1" dangerouslySetInnerHTML={{
            __html: richHtml
          }} data-id="element-2199" data-mp="6fwlxwwmyyrd" />}

            {/* TCPA consent — required */}
            <label className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-lg p-5 mb-6 cursor-pointer hover:bg-blue-100 transition-colors" data-id="element-2200" data-mp="3oskzsrbi84l4">
              {/* Carries Jornaya's disclosure id while this step is shown; the
                  contact-step disclosure is unmounted by now, so the id stays
                  unique on the page. */}
              <input id="leadid_tcpa_disclosure" type="checkbox" checked={schoolTcpaChecked} onChange={e => {
              setSchoolTcpaChecked(e.target.checked);
              if (e.target.checked) setSchoolError('');
            }} className="mt-1 w-5 h-5 text-[#e8594f] border-gray-300 rounded focus:ring-[#e8594f] flex-shrink-0" data-id="element-2201" data-mp="2zqub6wcrnvpa" />
              <span className="text-sm text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{
              __html: sanitizeTcpaHtml(tcpaText)
            }} data-id="element-2202" data-mp="3emjrqez6ngmm" />
            </label>

            {schoolError && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg" data-id="element-2203" data-mp="2lzyzlcavv0i0">
                <p className="text-red-600 text-sm" data-id="element-2204" data-mp="2qy0ls63lvfxp">{schoolError}</p>
              </div>}

            <div className="space-y-4" data-id="element-2205" data-mp="2vpilyrxt1chm">
              <button onClick={handleSchoolAgree} disabled={isSubmittingSchool || !schoolTcpaChecked} className="w-full py-4 bg-[#e8594f] hover:bg-[#d64a41] text-white font-bold rounded-lg text-lg transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2206" data-mp="2vqjyl0ugy71g">
                {isSubmittingSchool ? 'Submitting...' : 'I Agree & Continue'}
              </button>
              <button onClick={handleSchoolSkip} disabled={isSubmittingSchool} className="w-full py-3 bg-white hover:bg-gray-50 text-gray-700 font-semibold rounded-lg border-2 border-gray-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2207" data-mp="3upk5a2ly4iy">
                Skip This School
              </button>
            </div>
          </div>
        </div>
      </div>;
  }
  // Step 8: Additional matches via Clicks API. Same school-card design
  // as step 7 but with NO TCPA checkbox — clicking the CTA opens the
  // partner's clickURL in a new tab and advances to the next school.
  if (step === 8) {
    if (clicksLoading) {
      return <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-id="element-2208" data-mp="wg8t1kzqmrur">
          <div className="text-center" data-id="element-2209" data-mp="33am4714b5cue">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#e8594f] mb-4" data-id="element-2210" data-mp="2y9ydkgjzqy0p"></div>
            <p className="text-xl text-gray-700 font-semibold" data-id="element-2211" data-mp="1dsfg972ajrc3">
              Finding additional matches...
            </p>
          </div>
        </div>;
    }
    if (!clicksResults || clicksResults.length === 0) {
      window.location.href = `/?${buildExitParams().toString()}`;
      return null;
    }
    const currentClick: any = clicksResults[clicksIndex];
    const cSchoolName = currentClick.mBrandName || currentClick.brandName || currentClick.name || currentClick.schoolName || 'Partner School';
    const cSchoolLogo = currentClick.imageURL || currentClick.imageUrl || currentClick.logoUrl || currentClick.logo;
    // headContent is the marketing headline / program promo for this
    // school (e.g. "Earn a Degree in Psychology from Keiser®.").
    // It often differs from the school name and is the best signal of
    // what the user will see when they click through.
    const cHeadline = currentClick.headContent || currentClick.exactProgramName || currentClick.matchingProgramName || currentClick.programName || '';
    const cSchoolHighlight = currentClick.schoolHighlight || '';
    const cAboutText = currentClick.aboutBrand || currentClick.aboutCollege || currentClick.shortDescription || '';
    const cAccreditation = currentClick.accreditation || '';
    const cRichHtml = currentClick.bodyContent || '';
    const cClickURL = currentClick.clickURL || currentClick.clickUrl || currentClick.url || currentClick.link;
    return <div className="min-h-screen bg-gray-50" data-id="element-2212" data-mp="31qzre5jj2dcv">
        <div className="max-w-3xl mx-auto px-4 py-12" data-id="element-2213" data-mp="2wr3mm430ahw9">
          {/* Progress indicator */}
          <div className="text-center mb-8" data-id="element-2214" data-mp="2ryk7v3sl1gpv">
            <p className="text-xs uppercase tracking-widest text-[#e8594f] font-bold mb-1" data-id="element-2215" data-mp="2rx96famm0l1x">
              Additional Matches
            </p>
            <p className="text-sm text-gray-600 font-semibold" data-id="element-2216" data-mp="3kg6f66z6hv5j">
              School {clicksIndex + 1} of {clicksResults.length}
            </p>
            <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden max-w-xs mx-auto" data-id="element-2217" data-mp="3fnn0f6or8tz5">
              <div className="h-full bg-[#e8594f] transition-all duration-300" style={{
              width: `${(clicksIndex + 1) / clicksResults.length * 100}%`
            }} data-id="element-2218" data-mp="127v3to8ek2hd" />
            </div>
          </div>

          {/* School card */}
          <div className="relative bg-white rounded-xl shadow-sm border border-gray-200 p-8 md:p-10" data-id="element-2219" data-mp="1g5tdwhfoxcr2">
            {cSchoolHighlight && <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100 text-amber-900 text-xs font-semibold border border-amber-200 shadow-sm" data-id="element-2220" data-mp="1l5xue7glm7ha">
                <span aria-hidden="true" data-id="element-2221" data-mp="1uupk08y6sfp1">✨</span>
                {cSchoolHighlight}
              </span>}
            <div className="text-center mb-8" data-id="element-2222" data-mp="1ywsyz7k2e2f2">
              {cSchoolLogo && <img src={cSchoolLogo} alt={cSchoolName} className="h-28 md:h-32 mx-auto mb-6 object-contain" data-id="element-2223" data-mp="23vnpyws6qxa9" />}
              {cAboutText && <p className="text-base text-gray-600 leading-relaxed max-w-xl mx-auto" data-id="element-2224" data-mp="28txkr69zzvvc">
                  {cAboutText}
                </p>}
            </div>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-2" data-id="element-2225" data-mp="1jto4bsw8saqn">
              {cSchoolName}
            </h2>

            {/* Marketing headline from the Clicks API (headContent). Shown
                only when it provides additional info beyond the school
                name itself. */}
            {cHeadline && cHeadline.trim() !== cSchoolName.trim() && <p className="text-base md:text-lg text-[#e8594f] font-semibold text-center mb-3" data-id="element-2226" data-mp="111lky9s7ugcp">
                {cHeadline}
              </p>}

            {cAccreditation && <p className="text-xs text-gray-500 text-center mb-5" data-id="element-2227" data-mp="19txdq2asyfvu">
                Accredited by {cAccreditation}
              </p>}

            {cRichHtml && <div className="prose prose-sm max-w-none text-gray-700 mb-6 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mb-1" dangerouslySetInnerHTML={{
            __html: cRichHtml
          }} data-id="element-2228" data-mp="151dzd34ajgpz" />}

            <div className="space-y-4" data-id="element-2229" data-mp="d9ok1dqb18qd">
              <button onClick={handleClickThrough} disabled={!cClickURL} className="w-full py-4 bg-[#e8594f] hover:bg-[#d64a41] text-white font-bold rounded-lg text-lg transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2230" data-mp="36jbuv9e5jiy">
                Visit {cSchoolName} →
              </button>
              <button onClick={handleClicksSkip} className="w-full py-3 bg-white hover:bg-gray-50 text-gray-700 font-semibold rounded-lg border-2 border-gray-300 transition-all" data-id="element-2231" data-mp="3uedmy09grz3m">
                Skip This School
              </button>
            </div>
          </div>
        </div>
      </div>;
  }
  // Split-card layout: the marketing copy stays put while every question
  // (steps 0–6) is asked inside one persistent card beside it. Steps 7–8
  // (the school carousels) are handled above and shared with all layouts.
  if (layout === 'split-card') {
    return <div data-template-variant={variant} data-id="element-2232" data-mp="31oy9l3ubtu4j">
        {/* Hidden tracking fields populated by LeadID + TrustedForm scripts */}
        <LeadTrackingFields data-id="element-2233" data-mp="1z7wbjsnca9hu" />
        <RequestInfoSplitCard badge={content?.badge || 'Trusted by 70,000+ Students'} headline={content?.headline || 'Find Your Perfect'} headlineAccent={content?.headlineAccent || 'Online Degree'} subhead={content?.subhead || 'Get matched with accredited programs tailored to your goals. Compare schools, explore financial aid, and start your next chapter today.'} features={content?.features || ['Accredited institutions only', 'Financial aid guidance', 'Personalized program matches', 'No cost, no obligation']} step={step} stage={stage} formData={formData} availablePrograms={availablePrograms} years={years} error={error} tcpaLoading={tcpaLoading} tcpaError={tcpaError} tcpaDisclaimerHtml={tcpaResult?.tcpaDisclaimer ? sanitizeTcpaHtml(tcpaResult.tcpaDisclaimer) : null} addressChecking={addressChecking} onFieldChange={(field, value) => updateField(field as keyof FormData, value)} onAreaSelect={area => {
        updateField('areaOfStudy', area);
        updateField('program', 0);
      }} onZipChange={zip => {
        updateField('zipCode', zip);
        if (zip.length === 5) lookupZipCode(zip);
      }} onPhoneChange={handlePhoneChange} onContinue={handleContinue} onBack={handleBack} data-id="element-2234" data-mp="1z8o7x5orgb96" />
      </div>;
  }
  return <div className="min-h-screen bg-gray-50" data-template-variant={variant} data-id="element-2235" data-mp="kdo9wtki4kbb">
      {/* Hidden tracking fields populated by LeadID + TrustedForm scripts */}
      <LeadTrackingFields data-id="element-2236" data-mp="bbyhbkevvfby" />
      {/* Landing Page - Step 0 */}
      {step === 0 && <div data-id="element-2237" data-mp="6k6muyslwpgj">
          {/* Hero block — the optional photo + scrim sit behind this section
              only, so any bands below keep their own background. */}
          <div className="relative" data-id="element-2238" data-mp="3sroh1lcu4xgo">
          {content?.heroBackgroundImage && <div className="absolute inset-0 overflow-hidden" aria-hidden="true" data-id="element-2239" data-mp="3sthpn670drrb">
              <img src={content.heroBackgroundImage} alt="" className="w-full h-full object-cover" style={content.heroImageFlipped ? {
            transform: 'scaleX(-1)'
          } : undefined} data-id="element-2240" data-mp="3nra8m27bnli4" />
              {content.heroOverlay === 'none' ? null : content.heroOverlay === 'light' ? <div className="absolute inset-0 bg-white/70 lg:bg-gradient-to-r lg:from-white/90 lg:via-white/75 lg:to-white/40" data-id="element-2241" data-mp="3e5hfckkw96kb" /> : <div className="absolute inset-0 bg-gray-900/75 lg:bg-gradient-to-r lg:from-gray-900/90 lg:via-gray-900/70 lg:to-gray-900/40" data-id="element-2242" data-mp="306884aeo1msh" />}
            </div>}
          {/* Hero Section */}
          <div className={`relative max-w-7xl mx-auto px-4 py-6 md:py-16 lg:py-24 ${hasHeroImage ? 'text-white' : ''}`} data-id="element-2243" data-mp="3stm0dv7f5u69">
            {/* Mobile-only badge — stays above the form so the page is framed
             before the first question. */}
            <div className="lg:hidden text-center mb-4" data-id="element-2244" data-mp="3j1yahyqigl6w">
              <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-3 py-1.5 rounded-full text-xs font-semibold" data-id="element-2245" data-mp="3o23waya3ndh2">
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20" data-id="element-2246" data-mp="fl92s9f6z5fn">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-2247" data-mp="kfdboy80pnjd" />
                </svg>
                {content?.badge || 'Trusted by 70,000+ Students'}
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center" data-id="element-2248" data-mp="13ki7wsyd5h38">
              {/* Left Column - Content (below the form on mobile) */}
              <div className="text-center lg:text-left order-2 lg:order-1" data-id="element-2249" data-mp="auroc4dypmzq">
                {/* Mobile-only headline — sits under the form so the first
                 question stays above the fold on phones. */}
                <h1 className={`lg:hidden text-2xl sm:text-3xl font-bold leading-tight mb-5 ${hasHeroImage ? 'text-white' : 'text-gray-900'}`} data-id="element-2250" data-mp="ktqyxsiafspd">
                  {content?.headline || 'Find Your Perfect'}{' '}
                  <span className={hasHeroImage ? 'text-[#ffb4ad]' : 'text-[#e8594f]'} data-id="element-2251" data-mp="1w5i3k0gsg47c">
                    {content?.headlineAccent || 'Online Degree'}
                  </span>
                </h1>

                <div className="hidden lg:inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-semibold mb-6" data-id="element-2252" data-mp="sz73i5uh3xz">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" data-id="element-2253" data-mp="ap2fv6v7c0tw">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-2254" data-mp="eqnmiah57ivn" />
                  </svg>
                  {content?.badge || 'Trusted by 70,000+ Students'}
                </div>

                <h1 className={`hidden lg:block text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight ${hasHeroImage ? 'text-white' : 'text-gray-900'}`} data-id="element-2255" data-mp="tfjuy8n42xws">
                  {content?.headline || 'Find Your Perfect'}
                  <span className={`block ${hasHeroImage ? 'text-[#ffb4ad]' : 'text-[#e8594f]'}`} data-id="element-2256" data-mp="12i2v2kziiwfc">
                    {content?.headlineAccent || 'Online Degree'}
                  </span>
                </h1>

                <p className={`text-base md:text-xl mb-6 md:mb-8 leading-relaxed ${hasHeroImage ? 'text-gray-100' : 'text-gray-600'}`} data-id="element-2257" data-mp="2ryx1chbc84az">
                  {content?.subhead || 'Get matched with accredited programs tailored to your goals. Compare schools, explore financial aid, and start your journey today.'}
                </p>

                {/* Trust Signals */}
                

                {/* Features */}
                <div className="space-y-3 mb-8" data-id="element-2258" data-mp="3apxzj34mvvth">
                  {(content?.features || ['Accredited institutions only', 'Financial aid guidance', 'Personalized program matches', 'No application fees']).map((feature, idx) => <div key={idx} className={`flex items-center gap-3 ${hasHeroImage ? 'text-gray-100' : 'text-gray-700'}`} data-id="element-2259" data-mp="2wwq4t2l9ak7k" data-mpi={idx}>
                      <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" data-id="element-2260" data-mp="3uccekl15ddzg">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-2261" data-mp="3pklzy2v6g428" />
                      </svg>
                      <span className="font-medium" style={{
                    textAlign: "left"
                  }} data-id="element-2262" data-mp="hdfmcwwp2e6o">{feature}</span>
                    </div>)}
                </div>

              </div>

              {/* Right Column - Form (first on mobile, above the fold) */}
              <div className="bg-white rounded-2xl shadow-2xl p-5 sm:p-8 md:p-10 border border-gray-100 order-1 lg:order-2" data-id="element-2263" data-mp="3pqlilhj4kabj">
                <div className="text-center mb-5 md:mb-8" data-id="element-2264" data-mp="3ur0vy93ephti">
                  <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-1 md:mb-2" data-id="element-2265" data-mp="3gqq53623k38c">
                    {content?.formHeading || 'Get Started in 60 Seconds'}
                  </h2>
                  <p className="text-sm md:text-base text-gray-600" data-id="element-2266" data-mp="1ezd42s392fhk">
                    {content?.formSubhead || 'Tell us about your educational goals'}
                  </p>
                </div>

                <div className="space-y-4 md:space-y-5" data-id="element-2267" data-mp="33ig33ws4h8oi">
                  {includeDegreeInterest && <div data-id="element-2268" data-mp="2zh4e9th2orhd">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-2269" data-mp="2zhxaw1mbi0ls">
                        Degree of Interest <span className="text-red-500" data-id="element-2270" data-mp="3d9kkf359t5mi">*</span>
                      </label>
                      <select value={formData.degreeLevel} onChange={e => updateField('degreeLevel', Number(e.target.value))} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#e8594f] focus:border-[#e8594f] appearance-none cursor-pointer hover:border-gray-300 transition-colors" style={selectStyle} data-id="element-2271" data-mp="38hkcooiyl8fd">
                        <option value="" data-id="element-2272" data-mp="3r8k03lat2a6j">-- Please Select --</option>
                        {degreeLevelOptions.map(option => <option key={option.value} value={option.value} data-id="element-2273" data-mp="21su8d3e61jkz">
                            {option.label}
                          </option>)}
                      </select>
                    </div>}

                  {!lockedAreaId && <div data-id="element-2274" data-mp="1bxjrlmdsagv7">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-2275" data-mp="1byctoa1ssi70">
                      Area of Study <span className="text-red-500" data-id="element-2276" data-mp="1fydf4enbdtnk">*</span>
                    </label>
                    <select value={formData.areaOfStudy} onChange={e => {
                    updateField('areaOfStudy', Number(e.target.value));
                    updateField('program', 0);
                  }} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#e8594f] focus:border-[#e8594f] appearance-none cursor-pointer hover:border-gray-300 transition-colors" style={selectStyle} data-id="element-2277" data-mp="1krge52av1vb5">
                      <option value="" data-id="element-2278" data-mp="16zs372cclcvr">-- Please Select --</option>
                      {areaStudyOptions.map(option => <option key={option.value} value={option.value} data-id="element-2279" data-mp="2dgdkq85pm3o2">
                          {option.label}
                        </option>)}
                    </select>
                  </div>}

                  <div data-id="element-2280" data-mp="6rld3afa3pg2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-2281" data-mp="6qsdcteqm07f">
                      Program <span className="text-red-500" data-id="element-2282" data-mp="1tjs2jyalg1y">*</span>
                    </label>
                    <select value={formData.program} onChange={e => updateField('program', Number(e.target.value))} disabled={!formData.areaOfStudy} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#e8594f] focus:border-[#e8594f] appearance-none cursor-pointer disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed hover:border-gray-300 transition-colors" style={selectStyle} data-id="element-2283" data-mp="3t5v0bg7h2rf9">
                      <option value="" data-id="element-2284" data-mp="359h3sxc3qw2e">
                        {formData.areaOfStudy ? '-- Please Select --' : 'Select an area of study first'}
                      </option>
                      {availablePrograms.map(option => <option key={option.value} value={option.value} data-id="element-2285" data-mp="13hcja6wxn11p">
                          {option.label}
                        </option>)}
                    </select>
                  </div>

                  {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg" data-id="element-2286" data-mp="goe5oxrgbiku">
                      <p className="text-red-600 text-sm" data-id="element-2287" data-mp="bpjep8jbynpn">{error}</p>
                    </div>}

                  <button onClick={handleContinue} className="w-full py-4 bg-[#e8594f] hover:bg-[#d64a41] text-white font-bold rounded-lg text-lg transition-all shadow-lg hover:shadow-xl" data-id="element-2288" data-mp="6rrl8a31etjc">
                    Continue to Next Step →
                  </button>

                  <p className="text-center text-xs text-gray-500 mt-4" data-id="element-2289" data-mp="zduc21pti596">
                    🔒 Your information is secure and confidential
                  </p>
                </div>
              </div>
            </div>
          </div>
          </div>

          {/* Audience reassurance band (template-specific) */}
          {content?.supportNote && <section className="bg-white border-t border-gray-100 py-14" data-id="element-2290" data-mp="3a7ozov88ennh">
              <div className="max-w-5xl mx-auto px-4" data-id="element-2291" data-mp="1ppxq9w28gfoq">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 text-center" data-id="element-2292" data-mp="1priihf12rmjf">
                  {content.supportNote.heading}
                </h2>
                <p className="text-base text-gray-600 leading-relaxed max-w-3xl mx-auto text-center" data-id="element-2293" data-mp="1znlmkqfhlv90">
                  {content.supportNote.body}
                </p>
                {content.supportNote.points && <div className="grid md:grid-cols-3 gap-6 mt-10" data-id="element-2294" data-mp="29eg7nrgzyosv">
                    {content.supportNote.points.map(point => {
              const PointIcon = point.icon ? SUPPORT_NOTE_ICONS[point.icon] : null;
              return <div key={point.title} className="border border-gray-200 rounded-xl p-6 text-center" data-id="element-2295" data-mp="2ifmnrzfqhxll">
                        {PointIcon && <span className="mx-auto mb-4 flex w-11 h-11 items-center justify-center rounded-lg bg-[#e8594f]/10" data-id="element-2296" data-mp="2ncwo4a4nvto7">
                            <PointIcon className="w-5 h-5 text-[#e8594f]" aria-hidden="true" data-id="element-2297" data-mp="sznbi9gtjs1q" />
                          </span>}
                        <h3 className="text-base font-bold text-gray-900 mb-2" data-id="element-2298" data-mp="ky79mmzng8b4">
                          {point.title}
                        </h3>
                        <p className="text-sm text-gray-600 leading-relaxed" data-id="element-2299" data-mp="gzglv6h2q30o">
                          {point.body}
                        </p>
                      </div>;
            })}
                  </div>}
                {content.supportNote.footnote && <p className="text-xs text-gray-500 leading-relaxed max-w-3xl mx-auto text-center mt-8" data-id="element-2300" data-mp="13xberko2lhxi">
                    {content.supportNote.footnote}
                  </p>}
              </div>
            </section>}
        </div>}

      {/* Form Steps (1-6) */}
      {step > 0 && step <= 6 && <div className="max-w-4xl mx-auto px-4 py-6 md:py-12" data-id="element-2301" data-mp="18otjmjt7s2nr">
          {/* Progress Bar */}
          <div className="mb-6 md:mb-12" data-id="element-2302" data-mp="1nlkk6wemjkey">
            <div className="flex items-center justify-between mb-3 md:mb-4 max-w-2xl mx-auto gap-2" data-id="element-2303" data-mp="1nmlxl7j44izu">
              <div className="flex-1 text-center" data-id="element-2304" data-mp="1io0rwsl3d740">
                <div className={`text-[11px] sm:text-sm font-semibold mb-2 transition-colors ${step >= 1 && step < 6 ? 'text-[#e8594f]' : 'text-gray-400'}`} data-id="element-2305" data-mp="228sodd9imphf">
                  ABOUT YOU
                </div>
              </div>
              <div className="flex-1 text-center" data-id="element-2306" data-mp="1sb4vytzdtu2f">
                <div className={`text-[11px] sm:text-sm font-semibold mb-2 transition-colors ${step >= 6 ? 'text-[#e8594f]' : 'text-gray-400'}`} data-id="element-2307" data-mp="1scpneaqegww2">
                  CONTACT INFO
                </div>
              </div>
              <div className="flex-1 text-center" data-id="element-2308" data-mp="1nb0aepjma95c">
                <div className={`text-[11px] sm:text-sm font-semibold mb-2 transition-colors ${step > 6 ? 'text-[#e8594f]' : 'text-gray-400'}`} data-id="element-2309" data-mp="1nctjebhpd5gi">
                  SCHOOL MATCHES
                </div>
              </div>
            </div>

            {/* Progress Bar Track */}
            <div className="relative max-w-2xl mx-auto" data-id="element-2310" data-mp="zmpfs44il5pi">
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-2311" data-mp="zo0lwalfmpjs">
                <div className="h-full bg-[#e8594f] transition-all duration-500 ease-out" style={{
              width: `${step / 7 * 100}%`
            }} data-id="element-2312" data-mp="14jzgwh19n5t6" />
              </div>

              {/* Progress Dots */}
              <div className="absolute top-1/2 -translate-y-1/2 w-full flex justify-between px-0" data-id="element-2313" data-mp="22yxw06fieldy">
                <div className={`w-6 h-6 rounded-full border-4 transition-all ${step >= 1 ? 'bg-[#e8594f] border-[#e8594f]' : 'bg-white border-gray-300'}`} data-id="element-2314" data-mp="1y3ilnogpn7ma" />
                <div className={`w-6 h-6 rounded-full border-4 transition-all ${step >= 6 ? 'bg-[#e8594f] border-[#e8594f]' : 'bg-white border-gray-300'}`} data-id="element-2315" data-mp="1o8gwismfq5jp" />
                <div className={`w-6 h-6 rounded-full border-4 transition-all ${step > 6 ? 'bg-[#e8594f] border-[#e8594f]' : 'bg-white border-gray-300'}`} data-id="element-2316" data-mp="1f6r3xjgth0kc" />
              </div>
            </div>

            <p className="text-center text-sm text-gray-500 mt-4" data-id="element-2317" data-mp="39l0dbpl0ogsg">
              Step {showMilitaryStep || step <= 4 ? step : step - 1} of{' '}
              {showMilitaryStep ? 6 : 5}
            </p>
          </div>

          {/* Back Button */}
          <button onClick={handleBack} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 md:mb-6 transition-colors" data-id="element-2318" data-mp="3c4ornjhh4ra2">
            <ArrowLeftIcon className="w-5 h-5" data-id="element-2319" data-mp="3h26ssnp9227i" />
            <span className="font-medium" data-id="element-2320" data-mp="3lwb0xaa94ga6">Back</span>
          </button>

          {/* Form Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 sm:p-8 md:p-12" data-id="element-2321" data-mp="3qtb6zl73pj1a">
            {/* Step 1: Highest Education */}
            {step === 1 && <div className="max-w-xl mx-auto" data-id="element-2322" data-mp="2y5f9ie9pjn3g">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 text-center mb-6 md:mb-10" data-id="element-2323" data-mp="2y675vrb4pous">
                  What is your highest level of education?
                </h2>
                <select value={formData.highestEducation} onChange={e => updateField('highestEducation', e.target.value)} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none cursor-pointer hover:border-gray-300 transition-colors" style={selectStyle} data-id="element-2324" data-mp="385fxidiiurdz">
                  <option value="" data-id="element-2325" data-mp="2eih381f2chyv">-- Please Select --</option>
                  <option value="high-school" data-id="element-2326" data-mp="2shgnyxzggcpr">High School Diploma/GED</option>
                  <option value="some-college" data-id="element-2327" data-mp="2sfcmsnsxxrt6">Some College</option>
                  <option value="associates" data-id="element-2328" data-mp="2oh6jxqv83wdj">Associate's Degree</option>
                  <option value="bachelors" data-id="element-2329" data-mp="20prc5ikogmuw">Bachelor's Degree</option>
                  <option value="some-high-school" data-id="element-2330" data-mp="20nwp9ypuiko6">Some High School</option>
                </select>
              </div>}

            {/* Step 2: HS Graduation Year */}
            {step === 2 && <div className="max-w-xl mx-auto" data-id="element-2331" data-mp="29jvzhs0lrg5o">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 text-center mb-6 md:mb-10" data-id="element-2332" data-mp="24rcl4su3cgzt">
                  When did you graduate high school?
                </h2>
                <select value={formData.hsGraduationYear} onChange={e => updateField('hsGraduationYear', e.target.value)} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none cursor-pointer hover:border-gray-300 transition-colors" style={selectStyle} data-id="element-2333" data-mp="1d0ygln7ywh0t">
                  <option value="" data-id="element-2334" data-mp="swr3wligg8tr">-- Please Select --</option>
                  {years.map(year => <option key={year} value={year} data-id="element-2335" data-mp="xxyestfw9o4n">
                      {year}
                    </option>)}
                  <option value="1989" data-id="element-2336" data-mp="1lletgta8lkd4">1989 or earlier</option>
                </select>
              </div>}

            {/* Step 3: Start Date */}
            {step === 3 && <div className="max-w-xl mx-auto" data-id="element-2337" data-mp="1boa46kl3h8s3">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 text-center mb-6 md:mb-10" data-id="element-2338" data-mp="1go6a4pdcraaa">
                  When would you like to start classes?
                </h2>
                <select value={formData.startDate} onChange={e => updateField('startDate', e.target.value)} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#e8594f] focus:border-[#e8594f] appearance-none cursor-pointer hover:border-gray-300 transition-colors" style={selectStyle} data-id="element-2339" data-mp="c9biul41072">
                  <option value="" data-id="element-2340" data-mp="38q6wy78nqzl0">-- Please Select --</option>
                  <option value="immediately" data-id="element-2341" data-mp="34olbhxi25cz7">Immediately</option>
                  <option value="1-3-months" data-id="element-2342" data-mp="34qg1hq86rva5">1-3 months</option>
                  <option value="3-6-months" data-id="element-2343" data-mp="57n6995mo1sb">3-6 months</option>
                  <option value="6-12-months" data-id="element-2344" data-mp="9d8cqsiqn30">6-12 months</option>
                  <option value="1-year-plus" data-id="element-2345" data-mp="c7qucrz1xts">1+ year</option>
                  <option value="undecided" data-id="element-2346" data-mp="3rn83hwhv17r2">Undecided</option>
                </select>
              </div>}

            {/* Step 4: Military Affiliation (hidden in the v1 template,
                which posts "None" without asking) */}
            {step === 4 && showMilitaryStep && <div className="max-w-xl mx-auto" data-id="element-2347" data-mp="j87ni0vw9pjp">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 text-center mb-6 md:mb-10" data-id="element-2348" data-mp="j7g3lnbntg99">
                  Are you affiliated with the U.S. military?
                </h2>
                <select value={formData.militaryAffiliation} onChange={e => updateField('militaryAffiliation', e.target.value)} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#e8594f] focus:border-[#e8594f] appearance-none cursor-pointer hover:border-gray-300 transition-colors" style={selectStyle} data-id="element-2349" data-mp="26uu7cm4ezty5">
                  <option value="" data-id="element-2350" data-mp="2ps51xtf3pz79">-- Please Select --</option>
                  <option value="none" data-id="element-2351" data-mp="2ujmupu6z0th5">None</option>
                  <option value="active-duty" data-id="element-2352" data-mp="2uky72iehf9jv">Active Duty</option>
                  <option value="veteran" data-id="element-2353" data-mp="2ykotyb0w15j4">Veteran</option>
                  <option value="guard" data-id="element-2354" data-mp="33kus8a1nxqa6">Guard</option>
                  <option value="spouse" data-id="element-2355" data-mp="33m5s3yrzmdw0">Spouse</option>
                  <option value="dependent" data-id="element-2356" data-mp="38jo4y094ld9a">Dependent</option>
                  <option value="unknown" data-id="element-2357" data-mp="38k7cqo52al9l">Unknown</option>
                </select>
              </div>}

            {/* Step 5: Address (was step 4) */}
            {step === 5 && <div className="space-y-6 max-w-xl mx-auto" data-id="element-2358" data-mp="hptvs4mi7t24">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 text-center mb-6 md:mb-10" data-id="element-2359" data-mp="3ozndlzghs3g">
                  Where are you located?
                </h2>

                <div data-id="element-2360" data-mp="8q6vjm5igtan">
                  <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-2361" data-mp="wdnfo1imhhqi">
                    Street Address <span className="text-red-500" data-id="element-2362" data-mp="10f0jk625naus">*</span>
                  </label>
                  <input type="text" value={formData.streetAddress} onChange={e => updateField('streetAddress', e.target.value)} placeholder="123 Main St" className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-300 transition-colors" data-id="element-2363" data-mp="10h362g43mlur" />
                </div>

                {formData.city && formData.state && formData.zipCode ? <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3" data-id="element-2364" data-mp="1ti1iq5baat1o">
                    <span className="text-green-600 text-xl" data-id="element-2365" data-mp="1pgzjvpla4j08">📍</span>
                    <div className="flex-1" data-id="element-2366" data-mp="1pen49j2zukqc">
                      <p className="text-sm font-semibold text-gray-900" data-id="element-2367" data-mp="236t9490p55ah">
                        {formData.city}, {formData.state} {formData.zipCode}
                      </p>
                    </div>
                    <button type="button" onClick={() => {
              setFormData(prev => ({
                ...prev,
                city: '',
                state: '',
                zipCode: ''
              }));
            }} className="text-sm text-green-600 hover:text-green-700 font-semibold" data-id="element-2368" data-mp="1ak8ixbvyiz8i">
                      change
                    </button>
                  </div> : <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4" data-id="element-2369" data-mp="2vw9ysywxm830">
                    <div className="flex items-center gap-3 mb-3" data-id="element-2370" data-mp="2qxfh60apapkh">
                      <span className="text-yellow-600 text-xl" data-id="element-2371" data-mp="2qx768dy51ubx">📍</span>
                      <p className="text-sm font-semibold text-gray-700" data-id="element-2372" data-mp="2yxd3esdy97mx">
                        Enter your ZIP Code
                      </p>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-2373" data-mp="37yb9rhbv1r8o">
                      <input type="text" inputMode="numeric" value={formData.zipCode} onChange={e => {
                const val = e.target.value.replace(/\D/g, '').slice(0, 5);
                updateField('zipCode', val);
                if (val.length === 5) {
                  lookupZipCode(val);
                }
              }} placeholder="e.g. 90210" maxLength={5} className="flex-1 px-3 py-2 border border-yellow-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 bg-white" data-id="element-2374" data-mp="37wqd9m5zjy86" />
                      {formData.zipCode.length === 5 && !formData.city && <p className="text-xs text-red-500" data-id="element-2375" data-mp="1wje897yhp9kf">
                          ZIP not found. Try again.
                        </p>}
                    </div>
                  </div>}
              </div>}

            {/* Step 6: Contact Info (was step 5) */}
            {step === 6 && <div className="space-y-6 max-w-xl mx-auto" data-id="element-2376" data-mp="1p1gcs7d57z07">
                <div className="text-center mb-6 md:mb-8" data-id="element-2377" data-mp="1k0sbmsv7a1s9">
                  <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2" data-id="element-2378" data-mp="1jz7h338vyiue">
                    Last step! Where should we send your matches?
                  </h2>
                  <p className="text-gray-600" data-id="element-2379" data-mp="1b15i0t8pq09r">
                    We'll match you with top programs in {formData.city},{' '}
                    {formData.state}.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4" data-id="element-2380" data-mp="11b2kqguk605m">
                  <div data-id="element-2381" data-mp="wbzkratfqk4i">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-2382" data-mp="wcskhrtz89d5">
                      First Name <span className="text-red-500" data-id="element-2383" data-mp="rb3a8eekvzq4">*</span>
                    </label>
                    <input type="text" value={formData.firstName} onChange={e => updateField('firstName', e.target.value)} placeholder="John" className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e8594f] focus:border-[#e8594f] hover:border-gray-300 transition-colors" data-id="element-2384" data-mp="nbufb67sxvsz" />
                  </div>
                  <div data-id="element-2385" data-mp="137axecr85yuf">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-2386" data-mp="17agy74vkixti">
                      Last Name <span className="text-red-500" data-id="element-2387" data-mp="tb7jyaqxads6">*</span>
                    </label>
                    <input type="text" value={formData.lastName} onChange={e => updateField('lastName', e.target.value)} placeholder="Doe" className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e8594f] focus:border-[#e8594f] hover:border-gray-300 transition-colors" data-id="element-2388" data-mp="ya10yd5f4mx3" />
                  </div>
                </div>

                <div data-id="element-2389" data-mp="2ep27nge8ycw9">
                  <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-2390" data-mp="2emfzbejj7tcz">
                    Email Address <span className="text-red-500" data-id="element-2391" data-mp="2injf611kxpe7">*</span>
                  </label>
                  <input type="email" value={formData.email} onChange={e => updateField('email', e.target.value)} placeholder="john@example.com" className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e8594f] focus:border-[#e8594f] hover:border-gray-300 transition-colors" data-id="element-2392" data-mp="70g0t1g5hgs7" />
                </div>

                <div data-id="element-2393" data-mp="qpqde6klnlm3">
                  <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-2394" data-mp="qp6yyzu5rfct">
                    Phone Number <span className="text-red-500" data-id="element-2395" data-mp="lqnaoss3drha">*</span>
                  </label>
                  <input type="tel" value={formData.phone} onChange={handlePhoneChange} placeholder="555-123-4567" className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e8594f] focus:border-[#e8594f] hover:border-gray-300 transition-colors" data-id="element-2396" data-mp="zq53dj8idovx" />
                </div>

                <div className="pt-4" data-id="element-2397" data-mp="2wl17adi6hnxh">
                  {tcpaLoading ? <div className="flex items-center gap-3 text-sm text-gray-600" data-id="element-2398" data-mp="10gv3mq2bgf45">
                      <div className="w-5 h-5 border-2 border-gray-200 border-t-[#e8594f] rounded-full animate-spin" aria-label="Loading consent terms" data-id="element-2399" data-mp="15fesozc7i70q" />
                      <span data-id="element-2400" data-mp="1acy4lslfm7pi">Loading consent terms…</span>
                    </div> : tcpaError ? <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3" data-id="element-2401" data-mp="1egcudaz6a2g5">
                      {tcpaError}
                    </div> : tcpaResult?.tcpaDisclaimer ? <label className="flex items-start gap-3 cursor-pointer" data-id="element-2402" data-mp="1oe0tse7q3y4n">
                      {/* id="leadid_tcpa_disclosure" inside a <label> wrapping
                          the disclosure text is Jornaya's checkbox pattern —
                          it is how the shown disclosure gets associated with
                          the LeadiD token. */}
                      <input id="leadid_tcpa_disclosure" type="checkbox" checked={formData.consent} onChange={e => updateField('consent', e.target.checked)} className="mt-1 w-5 h-5 text-[#e8594f] border-gray-300 rounded focus:ring-[#e8594f]" data-id="element-2403" data-mp="223m3sz3jp30j" />
                      <span className="text-xs text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{
                __html: sanitizeTcpaHtml(tcpaResult.tcpaDisclaimer)
              }} data-id="element-2404" data-mp="3mn80vkc79h72" />
                    </label> : null}
                </div>
              </div>}

            {error && <div className="mt-6 p-3 bg-red-50 border border-red-200 rounded-lg max-w-xl mx-auto" data-id="element-2405" data-mp="e7wp6qiljs9j">
                <p className="text-red-600 text-sm" data-id="element-2406" data-mp="a6b44hvws7o9">{error}</p>
              </div>}

            <div className="mt-8 max-w-xl mx-auto" data-id="element-2407" data-mp="a8fg7n3yoczm">
              <button onClick={handleContinue} disabled={addressChecking} className="w-full py-4 bg-[#e8594f] hover:bg-[#d64a41] disabled:bg-[#e8594f]/60 disabled:cursor-wait text-white font-bold rounded-lg text-lg transition-colors shadow-lg hover:shadow-xl" data-id="element-2408" data-mp="5ekr245l75t5">
                {addressChecking ? 'Checking your address…' : step === 6 ? 'See Matching Colleges 🎓' : 'Continue →'}
              </button>

              {step === 6 && <p className="text-center text-gray-700 mt-4" style={{
            fontSize: '12.5px',
            lineHeight: '1.5'
          }} data-id="element-2409" data-mp="1g17wtpywadn">
                  By clicking "See Matching Colleges," you expressly consent to
                  receive autodialed and/or pre-recorded marketing text messages
                  (SMS/MMS) from FindOnlineDegrees.org at the number provided.
                  Consent is not a condition of any purchase. Msg &amp; data
                  rates may apply. Reply STOP to opt out. Consent is also given
                  for newsletters and email offers powered by Arcamax LLC. View
                  our{' '}
                  <a href="/privacy-policy" className="underline text-gray-700 hover:text-gray-900" data-id="element-2410" data-mp="f8qvv3p23pde">
                    Privacy Policy
                  </a>{' '}
                  and{' '}
                  <a href="/terms" className="underline text-gray-700 hover:text-gray-900" data-id="element-2411" data-mp="2zz9jsoc6ycre">
                    Terms &amp; Conditions
                  </a>
                  .
                </p>}

              {step !== 6 && <div className="mt-5 flex items-start gap-3 bg-blue-50 border border-blue-100 rounded-lg p-4 text-sm text-blue-800" data-id="element-2412" data-mp="2ngvy3vlgs07s">
                  <span className="text-blue-500 text-lg leading-none mt-0.5" data-id="element-2413" data-mp="32clffgfpfp66">
                    ℹ️
                  </span>
                  <span data-id="element-2414" data-mp="2xcqnrao3uvk2">
                    {step === 0 && 'Selecting your degree level and area of study helps us match you with the most relevant accredited programs.'}
                    {step === 1 && 'Your education background helps schools understand where you are in your academic journey and recommend the right program level.'}
                    {step === 2 && 'Knowing when you graduated helps us verify eligibility and connect you with programs that fit your timeline.'}
                    {step === 3 && 'Your preferred start date helps us find programs with upcoming enrollment windows that work for your schedule.'}
                    {step === 4 && 'Military affiliation may qualify you for special benefits, scholarships, and program options designed for service members and their families.'}
                    {step === 5 && 'Your location helps us find programs available in your area and determine eligibility for state-specific financial aid.'}
                  </span>
                </div>}
            </div>
          </div>
        </div>}
    </div>;
}